import { useEffect, useRef, useState } from 'react';
import { searchGroups, searchUsers } from '../graph/directory';
import { GraphError } from '../graph/client';
import type { PrincipalType, Store } from '../data/types';
import { useAppUser } from '../auth/AppUserContext';
import { getVisibleStores } from '../data/stores';
import { storeAccountLabel } from './people';
import { useFloatingList } from './useFloatingList';

/**
 * "90004 — Stonebridge" when `upn` is a store's shared account the viewer
 * can see, else null (round 1 2026-10-21: the task sheet's "Assign to"
 * showed the raw plus-address, cut off). Labels only — the value stays the
 * address. Inactive stores count (their accounts still read as the store).
 */
export function useStoreAccountLabel(upn: string): string | null {
  const { user } = useAppUser();
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setLabel(null);
    if (!upn.trim() || !user) return;
    getVisibleStores(user, { includeInactive: true })
      .catch(() => [] as Store[])
      .then((stores) => {
        if (live) setLabel(storeAccountLabel(stores, upn));
      });
    return () => {
      live = false;
    };
  }, [upn, user]);
  return label;
}

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Result {
  key: string;
  label: string;
  detail: string;
  value: string;
  displayName: string;
}

/**
 * THE standard identity input for the app: one combobox-style field that
 * searches the directory as you type and puts the resolved identity into
 * the field itself — users never need to know what a UPN or object ID is.
 *
 * - type "user": the field holds the sign-in email (typing one manually
 *   also works — search is an assist, not a gate).
 * - type "group": the field shows the group's NAME; the object ID is
 *   carried in `value` behind the scenes (pasting a raw ID also works).
 *
 * Degrades gracefully when directory search isn't consented: a notice
 * appears and manual entry continues to work.
 */
export function PrincipalField({
  type,
  value,
  displayValue,
  onChange,
  label,
  placeholder,
  required,
}: {
  type: PrincipalType;
  /** Stored value: UPN for users, group object id for groups. */
  value: string;
  /** Group display name shown in the box (groups only). */
  displayValue?: string | undefined;
  onChange: (change: { value: string; displayName?: string }) => void;
  label: string;
  placeholder?: string;
  required?: boolean;
}) {
  const [text, setText] = useState(type === 'group' ? displayValue || value : value);
  const [results, setResults] = useState<Result[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const suppressSearch = useRef(false);
  const requestSeq = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // A store account reads as its store while the box isn't being edited;
  // focusing shows (and edits) the address itself.
  // Only looked up while not typing (shown only then) — no Stores read per keystroke offline.
  const storeLabel = useStoreAccountLabel(type === 'user' && !focused ? value : '');
  const shownText = !focused && storeLabel && text.trim().toLowerCase() === value.trim().toLowerCase() ? storeLabel : text;

  // Follow external value changes (e.g. a form pre-fill) without searching.
  useEffect(() => {
    suppressSearch.current = true;
    setText(type === 'group' ? displayValue || value : value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // While the field is NOT focused, keep the text in step with the value
  // prop — a parent resetting its form (e.g. a Discard button) must revert
  // what the box shows, not just the stored value. Never while focused:
  // typing drives value for users, and syncing back would fight the caret.
  useEffect(() => {
    if (focused) return;
    const shown = type === 'group' ? displayValue || value : value;
    if (text !== shown) {
      suppressSearch.current = true;
      setText(shown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused, value, displayValue, type]);

  useEffect(() => {
    if (suppressSearch.current) {
      suppressSearch.current = false;
      return;
    }
    const q = text.trim();
    if (!focused || q.length < 2 || GUID_PATTERN.test(q)) {
      setResults(null);
      setSearching(false);
      return;
    }
    const seq = ++requestSeq.current;
    const timer = setTimeout(() => {
      setSearching(true);
      setNotice(null);
      const run =
        type === 'user'
          ? searchUsers(q).then((users) =>
              users.map(
                (u): Result => ({
                  key: u.id,
                  label: u.displayName,
                  detail: u.userPrincipalName,
                  value: u.userPrincipalName,
                  displayName: u.displayName,
                }),
              ),
            )
          : searchGroups(q).then((groups) =>
              groups.map(
                (g): Result => ({
                  key: g.id,
                  label: g.displayName,
                  detail: g.id,
                  value: g.id,
                  displayName: g.displayName,
                }),
              ),
            );
      run
        .then((r) => {
          if (seq === requestSeq.current) setResults(r);
        })
        .catch((err: unknown) => {
          if (seq !== requestSeq.current) return;
          setResults(null);
          if (
            (err instanceof GraphError && err.status === 403) ||
            (err instanceof Error && /consent|interaction/i.test(err.message))
          ) {
            setNotice(
              type === 'user'
                ? 'Directory search unavailable — you can type the full email address instead.'
                : 'Directory search unavailable — you can paste the group object ID instead.',
            );
          } else {
            setNotice(
              `Search failed: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        })
        .finally(() => {
          if (seq === requestSeq.current) setSearching(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, focused, type]);

  const pick = (r: Result) => {
    suppressSearch.current = true;
    setText(type === 'group' ? r.displayName : r.value);
    setResults(null);
    onChange({ value: r.value, displayName: r.displayName });
  };

  const onInput = (raw: string) => {
    setText(raw);
    const trimmed = raw.trim();
    if (type === 'user') {
      // The field IS the value; search is just an assist.
      onChange({ value: trimmed });
    } else if (GUID_PATTERN.test(trimmed)) {
      onChange({ value: trimmed });
    } else {
      // Free text for a group means "not resolved yet".
      onChange({ value: '', displayName: trimmed });
    }
  };

  const listOpen = focused && results !== null && !searching;
  const listStyle = useFloatingList(inputRef, listOpen);

  const resolvedGroupId =
    type === 'group' && GUID_PATTERN.test(value) && text !== value ? value : null;

  return (
    <label className="span-2 typeahead">
      {label}
      <input
        ref={inputRef}
        value={shownText}
        title={shownText !== text ? value : undefined}
        required={required ?? false}
        onChange={(e) => onInput(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder={
          placeholder ??
          (type === 'user' ? 'Type a name or email…' : 'Type a group name…')
        }
        autoComplete="off"
      />
      {searching && <div className="typeahead-status muted">Searching…</div>}
      {notice && <div className="typeahead-status error">{notice}</div>}
      {resolvedGroupId && (
        <div className="typeahead-status muted">✓ resolved: {resolvedGroupId}</div>
      )}
      {listOpen && (
        <div className="typeahead-results" style={listStyle}>
          {results.map((r) => (
            <button
              type="button"
              className="typeahead-result"
              key={r.key}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(r)}
            >
              <span>{r.label}</span>
              <span className="muted mono">{r.detail}</span>
            </button>
          ))}
          {results.length === 0 && (
            <div className="typeahead-status muted">No matches.</div>
          )}
        </div>
      )}
    </label>
  );
}
